import i18next from 'i18next';
import dayjs from 'dayjs';
import type { ApprovalAttributes } from '@/types/approval';
import { VEHICLE_COST_HR_MISSING_MESSAGE } from '@/utils/approval/vehicleCost';
import { formatAsDate } from '@/utils/format';

/** 연차휴가 — 레거시 appr_edit_forms_year_leave_v2 */
export const ANNUAL_LEAVE_REQUEST_TYPE = 32;

export const isAnnualLeaveRequestType = (apvType: number): boolean =>
  apvType === ANNUAL_LEAVE_REQUEST_TYPE;

/** D 연차 / A 오전 반차 / P 오후 반차 / B 오전 반반차 / C 오후 반반차 */
export type LeaveUnit = 'D' | 'A' | 'P' | 'B' | 'C';

export const LEAVE_UNIT_LABELS: Record<LeaveUnit, string> = {
  D: '연차',
  A: '오전 반차',
  P: '오후 반차',
  B: '오전 반반차',
  C: '오후 반반차',
};

const isLeaveUnit = (v: unknown): v is LeaveUnit =>
  typeof v === 'string' && v in LEAVE_UNIT_LABELS;

/** 레거시는 VN 법인에 반반차 없음 */
export const hasQuarterLeave = (companyCode: string | null | undefined) =>
  companyCode !== 'VN';

export const startUnitsOf = (companyCode: string | null | undefined) =>
  (hasQuarterLeave(companyCode)
    ? ['D', 'A', 'P', 'B', 'C']
    : ['D', 'A', 'P']) as LeaveUnit[];

/** 종료 구분은 연차·오전만 고를 수 있음. 시작일과 같은 날이면 시작 구분이 그대로 들어오므로 그 값은 보여 줌 */
export const endUnitsOf = (
  companyCode: string | null | undefined,
  current: LeaveUnit,
): LeaveUnit[] => {
  const base: LeaveUnit[] = hasQuarterLeave(companyCode)
    ? ['D', 'A', 'B']
    : ['D', 'A'];
  return base.includes(current) ? base : [...base, current];
};

/** 오전 반차·오전 반반차는 하루짜리라 종료일·종료 구분을 시작일에 묶음 */
export const isMorningOnlyUnit = (unit: LeaveUnit): boolean =>
  unit === 'A' || unit === 'B';

/** 근무시간 시작 시각. 종료는 +9 */
export const WORK_START_HOURS = [8, 9, 10] as const;
export const DEFAULT_WORK_START_HOUR = 9;
export const workEndHourOf = (startHour: number) => startHour + 9;

const pad2 = (n: number) => String(n).padStart(2, '0');
export const workTimeLabel = (startHour: number) =>
  `${pad2(startHour)}~${pad2(workEndHourOf(startHour))}`;

export type LeaveReason = 'A' | 'D';

export const LEAVE_REASONS: { value: LeaveReason; label: string }[] = [
  { value: 'A', label: '개인용무' },
  { value: 'D', label: '하계휴가' },
];

/** 레거시 rest_reason 선택 시 본문 전체를 이 문구로 교체 */
export const buildLeaveReasonContent = (reason: LeaveReason): string => {
  const label = LEAVE_REASONS.find((r) => r.value === reason)?.label ?? '';
  const t = (key: string) => i18next.t(key) || key;
  return `<p>&nbsp;</p><p>${t(
    '아래와 같은 사유로 위 기간동안 연차를 신청합니다.',
  )}&nbsp;</p><p>${t('사유')} : ${t(
    label,
  )}&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p>`;
};

export type LeaveSubtype = 'A01' | 'A02' | 'A03';

export const LEAVE_SUBTYPE_LABELS: Record<LeaveSubtype, string> = {
  A01: '연차 신청',
  A02: '반차 신청',
  A03: '반반차 신청',
};

/** 레거시 setSubject — 반반차가 반차보다 우선 */
export const leaveSubtypeOf = (
  startsUnit: LeaveUnit,
  endsUnit: LeaveUnit,
): LeaveSubtype => {
  const units = [startsUnit, endsUnit];
  if (units.some((u) => u === 'B' || u === 'C')) return 'A03';
  if (units.some((u) => u === 'A' || u === 'P')) return 'A02';
  return 'A01';
};

export type AnnualLeaveApplicant = {
  name: string;
  employeeNumber: string;
  /** 법인·사업부·부서를 공백으로 이은 값 */
  department: string;
  hireDate: string;
};

export type AnnualLeaveRequestFormState = {
  applicant: AnnualLeaveApplicant;
  workStartHour: number;
  startsAt: string;
  startsUnit: LeaveUnit;
  endsAt: string;
  endsUnit: LeaveUnit;
  /** 부여 이력이 없어 레거시가 입력란 없이 안내만 보이던 상태 — 사전 데이터 응답으로 채움 */
  noBalance: boolean;
};

export const createAnnualLeaveRequestState = (
  applicant: AnnualLeaveApplicant,
): AnnualLeaveRequestFormState => {
  const today = formatAsDate(dayjs());
  return {
    applicant,
    workStartHour: DEFAULT_WORK_START_HOUR,
    startsAt: today,
    startsUnit: 'D',
    endsAt: today,
    endsUnit: 'D',
    noBalance: false,
  };
};

type EmployeeSource = {
  name?: string | null;
  employeeNumber?: string | null;
  companyName?: string | null;
  divisionName?: string | null;
  departmentName?: string | null;
  joinedAt?: string | null;
};

export const toAnnualLeaveApplicant = (
  info: EmployeeSource | null | undefined,
): AnnualLeaveApplicant | null => {
  if (!info?.employeeNumber) return null;
  return {
    name: info.name ?? '',
    employeeNumber: info.employeeNumber,
    department: [info.companyName, info.divisionName, info.departmentName]
      .filter(Boolean)
      .join(' '),
    hireDate: info.joinedAt ? formatAsDate(info.joinedAt) : '',
  };
};

/** 내 인사 정보로 새 작성 상태. 인사 정보(사번)가 없으면 null */
export const annualLeaveRequestStateFromMyInfo = (
  info: EmployeeSource | null | undefined,
): AnnualLeaveRequestFormState | null => {
  const applicant = toAnnualLeaveApplicant(info);
  return applicant ? createAnnualLeaveRequestState(applicant) : null;
};

/** 레거시 startChange — 종료일은 시작일로 돌아가고 종료 구분은 시작 구분을 따름 */
export const changeStartsAt = (
  state: AnnualLeaveRequestFormState,
  startsAt: string,
): AnnualLeaveRequestFormState => ({
  ...state,
  startsAt,
  endsAt: startsAt,
  endsUnit: state.startsUnit,
});

/** 레거시 endChange — 다른 날이면 종료 구분을 연차로, 같은 날이면 시작 구분으로 */
export const changeEndsAt = (
  state: AnnualLeaveRequestFormState,
  endsAt: string,
): AnnualLeaveRequestFormState => ({
  ...state,
  endsAt,
  endsUnit: endsAt !== state.startsAt ? 'D' : state.startsUnit,
});

/** 레거시 date_from_opt change */
export const changeStartsUnit = (
  state: AnnualLeaveRequestFormState,
  startsUnit: LeaveUnit,
): AnnualLeaveRequestFormState => {
  if (isMorningOnlyUnit(startsUnit)) {
    return {
      ...state,
      startsUnit,
      endsAt: state.startsAt,
      endsUnit: startsUnit,
    };
  }
  return {
    ...state,
    startsUnit,
    endsUnit: state.startsAt === state.endsAt ? startsUnit : state.endsUnit,
  };
};

/** 작성자 언어 제목 — 레거시 `{구분} ({이름} / {시작일 구분}[ ~ {종료일 구분}])` */
export const buildAnnualLeaveRequestSubject = (
  state: AnnualLeaveRequestFormState,
): string => {
  const t = (key: string) => i18next.t(key) || key;
  const subtype = leaveSubtypeOf(state.startsUnit, state.endsUnit);
  const start = `${state.startsAt} ${t(LEAVE_UNIT_LABELS[state.startsUnit])}`;
  const range =
    state.startsAt === state.endsAt
      ? start
      : `${start} ~ ${state.endsAt} ${t(LEAVE_UNIT_LABELS[state.endsUnit])}`;
  return `${t(LEAVE_SUBTYPE_LABELS[subtype])} (${
    state.applicant.name
  } / ${range})`;
};

/** 이탈 경고 판정용 — 사용자가 고칠 수 있는 값만 */
export const annualLeaveRequestKey = (
  state: AnnualLeaveRequestFormState | null,
): string => {
  if (!state) return '';
  const { applicant: _applicant, noBalance: _noBalance, ...editable } = state;
  return JSON.stringify(editable);
};

export const ANNUAL_LEAVE_NO_BALANCE_MESSAGE =
  '[알림] 사용 가능한 휴가가 없습니다. 입사 후 1개월 근무 시 첫 월차가 부여됩니다.';

const DATE_FORMAT_MESSAGE =
  '날짜는 YYYY-MM-DD 형태로 입력하거나 달력에서 선택하셔야 합니다.';

const isDate = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);

/** 레거시 save_doc_rest_v2 검사(임시저장도 같음) */
export const checkAnnualLeaveRequestBeforeSave = (
  state: AnnualLeaveRequestFormState | null | undefined,
): string | null => {
  if (!state) return VEHICLE_COST_HR_MISSING_MESSAGE;
  if (state.noBalance) return ANNUAL_LEAVE_NO_BALANCE_MESSAGE;
  if (!isDate(state.startsAt) || !isDate(state.endsAt)) {
    return DATE_FORMAT_MESSAGE;
  }
  return null;
};

/** 휴가 유형은 연차(A) 고정 — 레거시 rest_type */
export const buildAnnualLeaveRequestAttributes = (
  state: AnnualLeaveRequestFormState | null | undefined,
): ApprovalAttributes | null => {
  if (!state) return null;
  return {
    leaveType: 'A',
    leaveSubtype: leaveSubtypeOf(state.startsUnit, state.endsUnit),
    startsAt: state.startsAt,
    startsUnit: state.startsUnit,
    endsAt: state.endsAt,
    endsUnit: state.endsUnit,
    workStartHour: state.workStartHour,
    workEndHour: workEndHourOf(state.workStartHour),
  };
};

export type AnnualLeaveRequestAttributes = {
  employeeNumber: string;
  leaveSubtype: LeaveSubtype | null;
  startsAt: string;
  startsUnit: LeaveUnit | null;
  endsAt: string;
  endsUnit: LeaveUnit | null;
  workStartHour: number | null;
  workEndHour: number | null;
};

/** 응답은 `{ name, value }` 객체, 요청은 코드 문자열 — 둘 다 받음 */
const codeOf = (v: unknown): string => {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const name = (v as Record<string, unknown>).name;
    return typeof name === 'string' ? name : '';
  }
  return '';
};

const hourOf = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isInteger(n) ? n : null;
};

// 응답 startsAt·endsAt 은 근무 시작·종료 시각의 UTC — KST 날짜로 바꿔야 하루 밀리지 않음
const kstDate = (v: unknown): string => {
  if (typeof v !== 'string' || !v) return '';
  if (isDate(v)) return v;
  const ms = Date.parse(v);
  return Number.isNaN(ms)
    ? ''
    : new Date(ms + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
};

/** 단건조회 attributes. 시작일이 없으면 null */
export const parseAnnualLeaveRequestAttributes = (
  attributes: unknown,
): AnnualLeaveRequestAttributes | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const a = attributes as Record<string, unknown>;
  const startsAt = kstDate(a.startsAt);
  if (!startsAt) return null;
  const subtype = codeOf(a.leaveSubtype);
  const startsUnit = codeOf(a.startsUnit);
  const endsUnit = codeOf(a.endsUnit);
  return {
    employeeNumber:
      typeof a.employeeNumber === 'string' ? a.employeeNumber : '',
    leaveSubtype:
      subtype in LEAVE_SUBTYPE_LABELS ? (subtype as LeaveSubtype) : null,
    startsAt,
    startsUnit: isLeaveUnit(startsUnit) ? startsUnit : null,
    endsAt: kstDate(a.endsAt) || startsAt,
    endsUnit: isLeaveUnit(endsUnit) ? endsUnit : null,
    workStartHour: hourOf(a.workStartHour),
    workEndHour: hourOf(a.workEndHour),
  };
};

/** 편집 복원 — 신청자는 내 인사 정보(편집은 작성자 본인만) */
export const annualLeaveRequestAttributesToState = (
  applicant: AnnualLeaveApplicant,
  saved: AnnualLeaveRequestAttributes,
): AnnualLeaveRequestFormState => {
  const startHour = saved.workStartHour ?? DEFAULT_WORK_START_HOUR;
  return {
    applicant,
    workStartHour: (WORK_START_HOURS as readonly number[]).includes(startHour)
      ? startHour
      : DEFAULT_WORK_START_HOUR,
    startsAt: saved.startsAt,
    startsUnit: saved.startsUnit ?? 'D',
    endsAt: saved.endsAt,
    endsUnit: saved.endsUnit ?? saved.startsUnit ?? 'D',
    noBalance: false,
  };
};

/** 레거시 prevQueryForREST — 저장된 문서는 휴가 시작일 연도(미래면 올해)의 사용 현황 */
export const leaveBalanceYearOf = (startsAt: string): number | undefined => {
  const year = Number(startsAt.slice(0, 4));
  if (!Number.isInteger(year) || year < 2000) return undefined;
  return Math.min(year, new Date().getFullYear());
};

export type LeaveBalance = {
  monthlyLeave: { granted: number; used: number };
  annualLeave: { granted: number; used: number };
  overage: number;
  remaining: number;
};

export type AnnualLeaveDraftSource = {
  attributes?: { employeeNumber?: string | null } | null;
  display?: {
    year?: number;
    leaveBalance?: LeaveBalance | null;
    useCount?: number | null;
  } | null;
};
